"use client";

import { useCallback, useMemo } from "react";
import Supercluster from "supercluster";

type BBox = [number, number, number, number];
type GeoJsonProperties = Record<string, unknown>;
type ClusterProperties = {
  cluster: true;
  cluster_id: number;
  point_count: number;
};

export type ClusterPoint<T> =
  | {
      isCluster: true;
      clusterId: number;
      count: number;
      lat: number;
      lng: number;
    }
  | {
      isCluster: false;
      item: T;
      lat: number;
      lng: number;
    };

export type UseMapClusteringOptions = {
  radius?: number;
  maxZoom?: number;
  minPoints?: number;
};

export type UseMapClusteringResult<T> = {
  clusters: ClusterPoint<T>[];
  getClusterExpansionZoom: (clusterId: number) => number;
};

const DEFAULT_OPTIONS: UseMapClusteringOptions = {
  radius: 60,
  maxZoom: 16,
  minPoints: 2,
};

/**
 * Client-side clustering using supercluster. Produces stable, world-tile-based
 * clusters that don't shift when the user pans or zooms incrementally.
 */
export function useMapClustering<T>(
  data: T[],
  coordsGetter: (item: T) => { lat: number; lng: number },
  bounds: { sw: { lat: number; lng: number }; ne: { lat: number; lng: number } } | null,
  zoom: number,
  options?: UseMapClusteringOptions,
): UseMapClusteringResult<T> {
  const opts = { ...DEFAULT_OPTIONS, ...options };

  const index = useMemo(() => {
    const sc = new Supercluster<{ index: number }, GeoJsonProperties>({
      radius: opts.radius,
      maxZoom: opts.maxZoom,
      minPoints: opts.minPoints,
    });

    const points: Supercluster.PointFeature<{ index: number }>[] = data.map(
      (item, i) => {
        const coords = coordsGetter(item);
        return {
          type: "Feature" as const,
          geometry: {
            type: "Point" as const,
            coordinates: [coords.lng, coords.lat],
          },
          properties: { index: i },
        };
      },
    );

    sc.load(points);
    return sc;
  }, [data, opts.radius, opts.maxZoom, opts.minPoints]);

  const getClusterExpansionZoom = useCallback(
    (clusterId: number): number => {
      return index.getClusterExpansionZoom(clusterId);
    },
    [index],
  );

  const clusters = useMemo(() => {
    if (!bounds) return [];

    const bbox: BBox = [
      bounds.sw.lng,
      bounds.sw.lat,
      bounds.ne.lng,
      bounds.ne.lat,
    ];

    const clusterFeatures = index.getClusters(bbox, Math.floor(zoom));

    return clusterFeatures.map((feature) => {
      const [lng, lat] = feature.geometry.coordinates;
      const properties = feature.properties as GeoJsonProperties;
      const isCluster = properties.cluster === true;

      if (isCluster) {
        const clusterProps = properties as ClusterProperties;
        return {
          isCluster: true as const,
          clusterId: clusterProps.cluster_id,
          count: clusterProps.point_count,
          lat,
          lng,
        };
      }

      const originalIndex = (properties as { index: number }).index;
      return {
        isCluster: false as const,
        item: data[originalIndex],
        lat,
        lng,
      };
    });
  }, [index, bounds, zoom, data]);

  return { clusters, getClusterExpansionZoom };
}
